<!--
  - SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<div class="calendar-list-wrapper">
		<CalendarListNew />
		<template v-if="!isPublic">
			<Draggable
				v-model="sortedCalendars.personal"
				itemKey="id"
				:disabled="disableDragging"
				v-bind="{ swapThreshold: 0.30, delay: 500, delayOnTouchOnly: true, touchStartThreshold: 3 }"
				draggable=".draggable-calendar-list-item"
				@update="updateInput">
				<template #item="{ element: calendar }">
					<CalendarListItem
						:key="calendar.id"
						class="draggable-calendar-list-item"
						:calendar="calendar" />
				</template>
			</Draggable>
		</template>
		<template v-else>
			<PublicCalendarListItem
				v-for="calendar in sortedCalendars.personal"
				:key="calendar.id"
				:calendar="calendar" />
		</template>

		<NcAppNavigationCaption v-if="sortedCalendars.shared.length" :name="$t('calendar', 'Shared calendars')" />
		<template v-if="!isPublic">
			<Draggable
				v-model="sortedCalendars.shared"
				itemKey="id"
				:disabled="disableDragging"
				v-bind="{ swapThreshold: 0.30, delay: 500, delayOnTouchOnly: true, touchStartThreshold: 3 }"
				draggable=".draggable-calendar-list-item"
				@update="updateInput">
				<template #item="{ element: calendar }">
					<CalendarListItem
						:key="calendar.id"
						class="draggable-calendar-list-item"
						:calendar="calendar" />
				</template>
			</Draggable>
		</template>
		<template v-else>
			<PublicCalendarListItem
				v-for="calendar in sortedCalendars.shared"
				:key="calendar.id"
				:calendar="calendar" />
		</template>

		<NcAppNavigationCaption v-if="sortedCalendars.deck.length" :name="$t('calendar', 'Deck')" />
		<template v-if="!isPublic">
			<Draggable
				v-model="sortedCalendars.deck"
				itemKey="id"
				:disabled="disableDragging"
				v-bind="{ swapThreshold: 0.30, delay: 500, delayOnTouchOnly: true, touchStartThreshold: 3 }"
				draggable=".draggable-calendar-list-item"
				@update="updateInput">
				<template #item="{ element: calendar }">
					<CalendarListItem
						:key="calendar.id"
						class="draggable-calendar-list-item"
						:calendar="calendar" />
				</template>
			</Draggable>
		</template>
		<template v-else>
			<PublicCalendarListItem
				v-for="calendar in sortedCalendars.deck"
				:key="calendar.id"
				:calendar="calendar" />
		</template>

		<NcAppNavigationCaption v-if="sortedCalendars.tasks.length" :name="$t('calendar', 'Tasks')" />
		<template v-if="!isPublic">
			<Draggable
				v-model="sortedCalendars.tasks"
				itemKey="id"
				:disabled="disableDragging"
				v-bind="{ swapThreshold: 0.30, delay: 500, delayOnTouchOnly: true, touchStartThreshold: 3 }"
				draggable=".draggable-calendar-list-item"
				@update="updateInput">
				<template #item="{ element: calendar }">
					<CalendarListItem
						:key="calendar.id"
						class="draggable-calendar-list-item"
						:calendar="calendar" />
				</template>
			</Draggable>
		</template>
		<template v-else>
			<PublicCalendarListItem
				v-for="calendar in sortedCalendars.tasks"
				:key="calendar.id"
				:calendar="calendar" />
		</template>

		<template v-if="!isPublic && isDelegationSupported && sortedCalendars.delegated.length">
			<template v-for="group in delegatedGroups" :key="group.delegatorUrl">
				<NcAppNavigationCaption
					:name="group.readOnly
						? $t('calendar', 'Delegated by {name} (read-only)', { name: group.displayname })
						: $t('calendar', 'Delegated by {name}', { name: group.displayname })" />
				<CalendarListItem
					v-for="calendar in group.calendars"
					:key="calendar.id"
					:calendar="calendar" />
			</template>
		</template>

		<NcAppNavigationSpacer />

		<!-- The header slot must be placed here, otherwise vuedraggable adds undefined as item to the array -->
		<template>
			<CalendarListItemLoadingPlaceholder v-if="loadingCalendars" />
		</template>
	</div>
</template>

<script>
import { showError } from '@nextcloud/dialogs'
import { NcAppNavigationCaption, NcAppNavigationSpacer } from '@nextcloud/vue'
import debounce from 'debounce'
import pLimit from 'p-limit'
import { mapState, mapStores } from 'pinia'
import draggable from 'vuedraggable'
import CalendarListItem from './CalendarList/CalendarListItem.vue'
import CalendarListItemLoadingPlaceholder from './CalendarList/CalendarListItemLoadingPlaceholder.vue'
import CalendarListNew from './CalendarList/CalendarListNew.vue'
import PublicCalendarListItem from './CalendarList/PublicCalendarListItem.vue'
import useCalendarsStore from '../../store/calendars.js'
import useDelegationStore from '../../store/delegation.ts'
import usePrincipalsStore from '../../store/principals.js'
import { isAfterVersion } from '../../utils/nextcloudVersion.ts'

const limit = pLimit(1)

export default {
	name: 'CalendarList',
	components: {
		CalendarListItem,
		CalendarListNew,
		CalendarListItemLoadingPlaceholder,
		PublicCalendarListItem,
		NcAppNavigationCaption,
		NcAppNavigationSpacer,
		Draggable: draggable,
	},

	props: {
		isPublic: {
			type: Boolean,
			required: true,
		},

		loadingCalendars: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			calendars: [],
			/**
			 * Calendars sorted by personal, shared, deck, and delegated
			 */
			sortedCalendars: {
				personal: [],
				shared: [],
				deck: [],
				tasks: [],
				delegated: [],
			},

			disableDragging: false,
			showOrderModal: false,
		}
	},

	computed: {
		...mapStores(useCalendarsStore, useDelegationStore, usePrincipalsStore),
		...mapState(useCalendarsStore, {
			serverCalendars: 'sortedCalendarsSubscriptions',
		}),

		loadingKeyCalendars() {
			return this._uid + '-loading-placeholder-calendars'
		},

		isDelegationSupported() {
			return isAfterVersion(34)
		},

		/**
		 * Delegated calendars grouped by the delegator (the user who granted
		 * proxy access), which may differ from each calendar's owner when the
		 * delegator only has access via a regular share.
		 *
		 * @return {Array<{delegatorUrl: string, displayname: string, calendars: object[]}>}
		 */
		delegatedGroups() {
			const groups = new Map()
			for (const calendar of this.sortedCalendars.delegated) {
				const delegatorUrl = calendar.delegatorUrl || calendar.owner || ''
				if (!groups.has(delegatorUrl)) {
					const principal = this.principalsStore.getPrincipalByUrl(delegatorUrl)
					groups.set(delegatorUrl, {
						delegatorUrl,
						displayname: principal?.displayname || principal?.userId || '',
						readOnly: !!calendar.readOnly,
						calendars: [],
					})
				}
				groups.get(delegatorUrl).calendars.push(calendar)
			}
			return Array.from(groups.values())
		},
	},

	watch: {
		serverCalendars(val) {
			this.calendars = val
		},

		calendars() {
			this.sortCalendars()
		},
	},

	mounted() {
		this.calendarsStore.$onAction(({ name, args, after }) => {
			if (name === 'toggleCalendarEnabled') {
				after(() => {
					const calendar = this.calendars.find((calendar) => calendar.id === args[0].calendar.id)
					calendar.enabled = args[0].calendar.enabled
					this.sortCalendars()
					this.update()
				})
			}
		})
	},

	methods: {
		sortCalendars() {
			this.sortedCalendars = {
				personal: [],
				shared: [],
				deck: [],
				tasks: [],
				delegated: [],
			}

			this.calendars.forEach((calendar) => {
				if (calendar.isDelegated) {
					this.sortedCalendars.delegated.push(calendar)
					return
				}

				if (calendar.isSharedWithMe) {
					this.sortedCalendars.shared.push(calendar)
					return
				}

				if (calendar.url.includes('app-generated--deck--board')) {
					this.sortedCalendars.deck.push(calendar)
					return
				}

				if (calendar.supportsTasks && !calendar.supportsEvents && !calendar.supportsJournals) {
					this.sortedCalendars.tasks.push(calendar)
					return
				}

				this.sortedCalendars.personal.push(calendar)
			})
		},

		updateInput: debounce(async function() {
			await this.update()
		}, 2500),

		async update() {
			this.disableDragging = true
			const currentCalendars = [
				...this.sortedCalendars.personal,
				...this.sortedCalendars.shared,
				...this.sortedCalendars.deck,
				...this.sortedCalendars.tasks,
			]
			const newOrder = currentCalendars.reduce((newOrderObj, currentItem, currentIndex) => {
				newOrderObj[currentItem.id] = currentIndex
				return newOrderObj
			}, {})

			this.calendars = currentCalendars

			try {
				await limit(() => this.calendarsStore.updateCalendarListOrder({ newOrder }))
			} catch (err) {
				showError(this.$t('calendar', 'Could not update calendar order.'))
				// Reset calendar list order on error
				this.calendars = this.calendarsStore.sortedCalendarsSubscriptions
			} finally {
				this.disableDragging = false
			}
		},
	},
}
</script>
